// Phone numbers (Mitch, 2026-11-03: "Phone numbers need to also be formatted correctly across the board, so whitespace is
// removed etc. And when viewing a phone number it's clean with the ( ) and country code"; his choice: each country's own
// style). Saved one way, E.164 (+12155550134, +8613800138000; an extension after it: "+12155550134 ext. 12"), read
// from however it was typed with the country it's most likely in (the vendor's, else the US), shown as its country
// writes it internationally: +1 (215) 555-0134 for the US and Canada, +86 138 0013 8000, +44 20 7946 0958.
// libphonenumber-js (Google's phone number rules, MIT) does the reading and the formats.
import { type CountryCode, getCountries, parsePhoneNumberFromString } from "libphonenumber-js";

const KNOWN = new Set<string>(getCountries());
const asCountry = (cc?: string | null): CountryCode | undefined => {
  const c = (cc ?? "").toUpperCase();
  return KNOWN.has(c) ? (c as CountryCode) : undefined;
};

/**
 * A phone number as typed ("215 555 0134", "+86 138-0013-8000", "(0)20 7946 0958 x12"), read with the country it's most
 * likely in: `value` is how it's saved (E.164, with " ext. N"), "" for nothing; `ok` false with `error` when it isn't
 * a phone number of that country (too short, too long, not one Google's rules know).
 */
export function cleanPhone(text: string, country?: string | null): { value: string; ok: boolean; error?: string } {
  const t = text.trim();
  if (!t) return { value: "", ok: true };
  const p = parsePhoneNumberFromString(t, { defaultCountry: asCountry(country) ?? "US", extract: false });
  if (!p?.isValid()) {
    const digits = t.replace(/[^\d+]/g, "");
    const where = t.startsWith("+") ? "" : ` in ${asCountry(country) ?? "US"} (start it with + and its country code if it's from elsewhere)`;
    return { value: digits, ok: false, error: `${t} isn't a phone number${where}` };
  }
  return { value: `${p.number}${p.ext ? ` ext. ${p.ext}` : ""}`, ok: true };
}

/** A saved phone number as its country writes it internationally: +1 (215) 555-0134, +86 138 0013 8000 (and its
 * extension). Anything it can't read is shown as it is. */
export function phoneText(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v) return "";
  const [num, ext] = v.split(/\s*ext\.\s*/i);
  const p = parsePhoneNumberFromString(num);
  if (!p) return v;
  const shown = p.countryCallingCode === "1" ? `+1 ${p.formatNational()}` : p.formatInternational();
  return ext ? `${shown} ext. ${ext}` : shown;
}

/** A tel: link for it (no spaces), or null. */
export const phoneHref = (value: string | null | undefined) => {
  const num = (value ?? "").split(/\s*ext\.\s*/i)[0].trim();
  return num ? `tel:${num}` : null;
};
